import { useQuery } from '@tanstack/react-query';
import { useRecipient } from '../components/settings/shared/settings-queries';
import { API_BASE } from './api-base';

/**
 * The shared employee release is conducted by the authenticated server. No client override.
 *
 * A request that FAILS throws rather than answering `false`. The gate re-checks on every window focus,
 * and the first request after a laptop wakes is exactly the one that times out — answered as `false`, it
 * switched a live chat out of the Chats transcript mid-read, repainted the thread from the other mode's
 * stale cache (a landed picture came back as its placeholder, game and all) and switched back a second
 * later. Thrown, React Query keeps the last real answer; a first check that fails still resolves closed.
 */
export async function fetchChatsEnabled(): Promise<boolean> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 3500);
  try {
    const response = await fetch(`${API_BASE}/api/flags/chats`, {
      credentials: 'include', signal: controller.signal,
    });
    if (!response.ok) throw new Error(`chats flag ${response.status}`);
    return (await response.json()) === true;
  } finally {
    clearTimeout(timeout);
  }
}

/** One cached request per account, shared by the menu, layout and transcript. */
export function useChatsGate(): { enabled: boolean; resolved: boolean } {
  const recipient = useRecipient();
  const owner = recipient.data?.email && recipient.data.metaSiteId
    ? JSON.stringify([recipient.data.email, recipient.data.metaSiteId]) : null;
  const query = useQuery({
    queryKey: ['chats-flag', owner], queryFn: fetchChatsEnabled, enabled: !!owner,
    staleTime: 30_000, refetchOnWindowFocus: true, retry: false,
  });
  return { enabled: !!owner && query.data === true,
    resolved: owner ? query.isSuccess || query.isError : !recipient.isLoading };
}
